import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Stethoscope, ClipboardList, Pill, Users, ShieldCheck,
  FileBarChart, HeartPulse, Handshake, ArrowRight, ArrowUpRight,
  Scale, Landmark, Eye,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";

const GREEN = "#0F382C";
const GOLD = "#C5A059";

const Overline = ({ children, light = false }) => (
  <p className={`font-mono-brand text-[11px] uppercase tracking-[0.22em] ${light ? "text-[#C5A059]" : "text-[#0F382C]"}`}>
    {children}
  </p>
);

const VALUES = [
  { icon: Scale, title: "Governance before growth",
    text: "Every service operates under named clinical oversight, professional competency requirements and clear escalation routes — before any expansion is considered." },
  { icon: ShieldCheck, title: "Standards without exception",
    text: "Registration checks, safeguarding awareness and defined scopes of practice apply to every clinician, on every engagement, without shortcuts." },
  { icon: HeartPulse, title: "Patients at the centre",
    text: "Structured, unhurried clinical contact that puts the patient's experience and safety first — the measure of every service we run." },
  { icon: Handshake, title: "Partnership, not supply",
    text: "We integrate with your existing NHS teams — practice staff, PCN colleagues and community services — rather than working around them." },
];

const PRACTICE = [
  { icon: Stethoscope, title: "We understand primary care", text: "NCS is built around the operational reality of GP practices and PCNs — appointment systems, prescribing workflows, QOF and network priorities." },
  { icon: ClipboardList, title: "Structured service delivery", text: "Defined scopes, delivery plans and milestones. You always know what is being delivered, by whom, and when." },
  { icon: Pill, title: "Medicines optimisation expertise", text: "Clinical pharmacy is our core discipline — from SMRs and deprescribing to prescribing safety programmes." },
  { icon: Users, title: "Flexible workforce solutions", text: "From a single session to a full clinical pharmacy team, shaped around your budget, estate and patient needs." },
  { icon: ShieldCheck, title: "Clinical governance", text: "Every service operates under named clinical oversight, professional competency requirements and clear escalation routes." },
  { icon: FileBarChart, title: "Reporting & accountability", text: "Activity and outcome reporting as standard — evidence your board, federation or ICB can rely on." },
  { icon: Eye, title: "Active service oversight", text: "Delivery, communication and quality are monitored on every engagement — not reviewed after the fact." },
  { icon: Landmark, title: "Institutional accountability", text: "Clear lines of responsibility from the clinician delivering care to the organisation accountable for it." },
];

const About = () => (
  <main data-testid="about-page">
    <Seo
      title="About National Clinical Services | NCS"
      description="National Clinical Services is a clinically led UK provider of clinical pharmacy services and workforce solutions for primary care and NHS organisations."
    />

    {/* EDITORIAL HERO */}
    <section className="relative overflow-hidden pt-36 sm:pt-44 pb-16 sm:pb-24 border-b border-[#0B192C]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Overline>About National Clinical Services</Overline>
        <h1
          data-testid="about-heading"
          className="mt-5 font-display font-medium tracking-tight leading-[1.08] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-4xl"
        >
          <MaskedLine delay={0.15}>Clinically led.</MaskedLine>
          <MaskedLine delay={0.3}>Governed by <span className="font-serif-accent">principle.</span></MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl"
        >
          National Clinical Services Ltd is a UK-based clinical services provider supporting Primary
          Care Networks, GP practices, GP federations, Integrated Care Boards and NHS healthcare
          organisations. We exist to give primary care access to high-quality clinical pharmacy
          capacity — through managed clinical services we deliver for you, and flexible workforce
          solutions that work within your team.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-4 text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl"
        >
          Whether commissioning a Structured Medication Review programme, a prescribing safety
          project or requesting experienced pharmacists and pharmacy technicians, every engagement
          is clinically led, governed and reported — because in primary care, trust is earned
          through delivery.
        </motion.p>
      </div>
    </section>

    {/* THE ETHOS WE RUN */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-b border-[#0B192C]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>The ethos we run</Overline>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-2xl">
              Values that govern every division, every clinician, every day
            </h2>
            <p className="text-sm leading-relaxed text-[#475569] max-w-md">
              NCS is deliberately built as an institution, not an individual. These four commitments
              define how the organisation operates — in clinical services, transport, out-of-hours
              care and beyond.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={(i % 4) * 0.07}>
              <div data-testid={`value-card-${i + 1}`}
                className={`h-full bg-[#FBFBFA] border border-[#0B192C]/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_-18px_rgba(11,25,44,0.18)] ${i % 2 === 0 ? "hairline-gold" : "hairline-green"}`}>
                <span className="flex items-center justify-between">
                  <v.icon size={22} className="text-[#0F382C]" />
                  <span className="font-mono-brand text-[10px] tracking-[0.18em] text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
                </span>
                <h3 className="mt-6 font-display text-xl font-semibold text-[#0B192C] leading-snug">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#475569]">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* OUR PEOPLE AT WORK (imagery, no founder spotlight) */}
    <section className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Our clinicians at work</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-2xl">
              Experienced professionals, embedded where care happens
            </h2>
          </div>
          <p className="text-sm leading-relaxed text-[#475569] max-w-md">
            Behind every NCS engagement is a clinician who knows primary care from the inside —
            the rota gaps, the prescribing backlogs, the care home rounds that can't wait.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Reveal>
            <div className="overflow-hidden border border-[#0B192C]/10">
              <img
                src="/assets/about-pharmacist-consultation.png"
                alt="An NCS clinical pharmacist consulting an elderly patient about their medicines in a GP practice"
                className="w-full h-[280px] object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden border border-[#0B192C]/10">
              <img
                src="/assets/about-pharmacist-medicines-review.png"
                alt="A pharmacist completing a structured medication review at a GP practice desk"
                className="w-full h-[280px] object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="overflow-hidden border border-[#0B192C]/10">
              <img
                src="/assets/about-pharmacist-practice-team.png"
                alt="A pharmacist working alongside GPs and nurses in a primary care practice team"
                className="w-full h-[280px] object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* OUR INTERNATIONAL VISION */}
    <section className="pb-20 sm:pb-28 lg:pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div
            data-testid="about-international-vision"
            className="relative overflow-hidden bg-[#0B192C] border-t-2 border-[#C5A059] px-8 py-12 sm:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
          >
            <div className="relative lg:col-span-7">
              <Overline light>Our International Vision</Overline>
              <div className="mt-5 space-y-4 text-base sm:text-lg leading-relaxed text-white/75">
                <p>
                  National Clinical Services was founded with the ambition to create a dependable,
                  clinically led organisation that delivers meaningful services. As we continue to
                  develop our presence in the United Kingdom, our long-term vision includes building
                  healthcare partnerships internationally.
                </p>
                <p>
                  With an initial focus on Dubai, Doha and Oman, we are exploring opportunities to
                  share our experience in clinical pharmacy, medicines optimisation and healthcare
                  workforce development. Our ambition is to build lasting relationships with
                  healthcare organisations that value professionalism, patient safety and service
                  excellence.
                </p>
              </div>
              <p className="mt-6 font-display text-sm italic text-white/50">— The NCS Group</p>
              <Link
                to="/international"
                data-testid="about-cta-international"
                className="group mt-7 inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-semibold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white"
              >
                Explore NCS International
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="relative lg:col-span-5">
              <div className="overflow-hidden border border-white/15">
                <img
                  src="/assets/skyline-dubai.png"
                  alt="The Dubai skyline at golden hour — a focus market for NCS International"
                  loading="lazy"
                  className="w-full h-[260px] sm:h-[320px] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 left-6 flex items-center gap-2 bg-white border border-[#0B192C]/10 shadow-[0_12px_30px_-12px_rgba(11,25,44,0.2)] px-4 py-3">
                <img src="https://flagcdn.com/w80/ae.png" alt="UAE flag" loading="lazy" className="w-6 h-6 rounded-full object-cover ring-1 ring-black/10" />
                <img src="https://flagcdn.com/w80/qa.png" alt="Qatar flag" loading="lazy" className="w-6 h-6 rounded-full object-cover ring-1 ring-black/10" />
                <img src="https://flagcdn.com/w80/om.png" alt="Oman flag" loading="lazy" className="w-6 h-6 rounded-full object-cover ring-1 ring-black/10" />
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.14em] text-[#0B192C] pl-1">UAE · Qatar · Oman</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* HOW WE WORK */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#0B192C]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>How we work</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C]">
            The principles behind every engagement
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0B192C]/10 border border-[#0B192C]/10">
          {PRACTICE.map((p, i) => (
            <Reveal key={p.title} delay={(i % 4) * 0.07}>
              <div
                data-testid={`pillar-card-${i + 1}`}
                className="h-full bg-white p-7 transition-colors duration-300 hover:bg-[#F4F4F0]"
              >
                <span className="flex items-center justify-between">
                  <p.icon size={20} className="text-[#0F382C]" />
                  <span className="font-mono-brand text-[10px] tracking-[0.18em] text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#0B192C] leading-snug">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden bg-[#0F382C] px-8 py-14 sm:px-16 text-center">
            <img src="/assets/ncs-mark.png" alt="" className="absolute -right-14 -bottom-16 w-64 opacity-10 rotate-12 pointer-events-none" />
            <div className="relative py-4">
              <Overline light>Work with us</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white max-w-2xl mx-auto">
                Let's strengthen your clinical capacity
              </h2>
              <p className="mt-4 text-base text-white/60 max-w-xl mx-auto">
                Commission a clinical service, request workforce — or both in a single enquiry.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/commission-clinical-services"
                  data-testid="about-cta-commission"
                  className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-semibold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white"
                >
                  Commission Clinical Services
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/request-clinical-workforce"
                  data-testid="about-cta-workforce"
                  className="inline-flex items-center gap-2.5 text-white font-medium px-7 py-4 rounded-full text-sm border border-white/30 transition-colors duration-300 hover:bg-white hover:text-[#0B192C]"
                >
                  Request Clinical Workforce
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default About;
